import { create } from 'zustand';

export type ThemeMode = 'light' | 'dark';
export type ReadabilityMode = 'standard' | 'clinical';

const STORAGE_KEY = 'aiemd-theme';
const CLINICAL_MODE_KEY = 'aiemd-clinical-mode';

interface ThemeState {
  themeMode: ThemeMode;
  isDark: boolean;
  readabilityMode: ReadabilityMode;
  isClinicalMode: boolean;

  setTheme: (mode: ThemeMode) => void;
  toggleTheme: () => void;
  setClinicalMode: (enabled: boolean) => void;
  toggleClinicalMode: () => void;
  getSystemPreference: () => boolean;
  initTheme: () => void;
}

/** Reads the OS-level dark-mode preference (matches Angular Theme.getSystemPreference). */
function getSystemPreference(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

/** Applies the data-theme attribute and `.dark` class exactly like Angular's updateTheme(). */
function applyTheme(mode: ThemeMode): void {
  const root = document.documentElement;
  if (mode === 'dark') {
    root.setAttribute('data-theme', 'dark');
    root.classList.add('dark');
  } else {
    root.setAttribute('data-theme', 'light');
    root.classList.remove('dark');
  }
}

/** Applies readability attribute, class, and inline CSS vars exactly like Angular's updateClinicalMode(). */
function applyClinicalMode(isClinical: boolean): void {
  const root = document.documentElement;
  if (isClinical) {
    root.setAttribute('data-readability', 'clinical');
    root.classList.add('clinical-mode');
    root.style.setProperty('--font-size-base', '1.25rem'); // 20px
    root.style.setProperty('--line-height-normal', '1.8');
    root.style.setProperty('--spacing-md', '1.25rem');
    root.style.setProperty('--font-weight-normal', '450');
  } else {
    root.setAttribute('data-readability', 'standard');
    root.classList.remove('clinical-mode');
    root.style.setProperty('--font-size-base', '1.125rem'); // 18px
    root.style.setProperty('--line-height-normal', '1.6');
    root.style.setProperty('--spacing-md', '1rem');
    root.style.setProperty('--font-weight-normal', '400');
  }
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  themeMode: 'light',
  isDark: false,
  readabilityMode: 'standard',
  isClinicalMode: false,

  getSystemPreference,

  setTheme: (mode) => {
    // Replaces Angular's effect() watcher: persist + apply DOM immediately inside the action.
    const isDark = mode === 'dark';
    applyTheme(mode);
    localStorage.setItem(STORAGE_KEY, mode);
    set({ themeMode: mode, isDark });
  },

  toggleTheme: () => {
    const current = get().themeMode;
    get().setTheme(current === 'light' ? 'dark' : 'light');
  },

  setClinicalMode: (enabled) => {
    const mode: ReadabilityMode = enabled ? 'clinical' : 'standard';
    localStorage.setItem(CLINICAL_MODE_KEY, mode);
    applyClinicalMode(enabled);
    set({ readabilityMode: mode, isClinicalMode: enabled });
  },

  toggleClinicalMode: () => {
    get().setClinicalMode(!get().isClinicalMode);
  },

  // Runs the Angular constructor logic (initializeTheme + initializeClinicalMode) once on mount.
  initTheme: () => {
    // initializeTheme()
    const savedTheme = localStorage.getItem(STORAGE_KEY);
    let initialTheme: ThemeMode;
    if (savedTheme === 'light' || savedTheme === 'dark') {
      initialTheme = savedTheme;
    } else {
      initialTheme = getSystemPreference() ? 'dark' : 'light';
    }
    applyTheme(initialTheme);
    set({ themeMode: initialTheme, isDark: initialTheme === 'dark' });

    // initializeClinicalMode()
    const savedMode = localStorage.getItem(CLINICAL_MODE_KEY);
    if (savedMode === 'clinical') {
      applyClinicalMode(true);
      set({ readabilityMode: 'clinical', isClinicalMode: true });
    }
  },
}));
